import React from 'react';
import { ConfigProvider, App } from 'antd';
import { AuthProvider } from './hooks/useAuth';
import { RequestProvider } from './hooks/useRequest';
import { AutoRequestProvider } from './hooks/useAutoRequest';
import { IdDemandProvider } from './hooks/useIdDemand';
import { SourceProvider } from './hooks/useSource';
import { PushReprocessProvider } from './hooks/usePushReprocess';
import { PushResendCallbacksProvider } from './hooks/usePushResendCallbacks';
import { ApiCallbackProvider } from './hooks/useApiCallback';
import { AbrangenciaProvider } from './hooks/useAbrangencia';
import { DatateamReprocessProvider } from './hooks/useDatateamReprocess';
import { AutoRequestViewProvider } from './hooks/useAutoRequestView';
import { RequestViewProvider } from './hooks/useRequestView';
import { PushViewProvider } from './hooks/usePushView';
import { ThemeProvider, useTheme } from './hooks/useTheme';
import { lightTheme, darkTheme } from './config/theme';
import { AuthenticatedSystemProviders } from './hooks/AuthenticatedSystemProviders';

/**
 * Provider interno que aplica o tema do Ant Design.
 * Deve estar dentro do ThemeProvider para acessar o contexto.
 */
const AntDesignProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme } = useTheme();
  const currentTheme = theme === 'light' ? lightTheme : darkTheme;

  return (
    <ConfigProvider theme={currentTheme}>
      <App>
        {children}
      </App>
    </ConfigProvider>
  );
};

/**
 * Provider principal que envolve toda a aplicação.
 * 
 * Ordem dos providers:
 * 1. ThemeProvider - gerencia tema light/dark
 * 2. AntDesignProvider - aplica tema no Ant Design
 * 3. AuthProvider - gerencia autenticação
 * 4. RequestProvider - gerencia requisições
 * 5. IdDemandProvider - gerencia demandas
 * 6. SourceProvider - gerencia fontes
 * 7. PushReprocessProvider - gerencia reprocessamento do Push
 * 8. PushResendCallbacksProvider - gerencia reenvio de callbacks do Push
 * 9. ApiCallbackProvider - gerencia busca de callbacks da API
 * 10. AutoRequestProvider - gerencia auto requests
 * 11. AbrangenciaProvider - gerencia abrangências do CapturaWeb
 * 12. AutoRequestViewProvider - gerencia visualização de autorequests
 * 13. RequestViewProvider - gerencia visualização de requests
 * 14. PushViewProvider - gerencia visualização de pushes
 */
const Providers: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ThemeProvider>
      <AntDesignProvider>
    <AuthProvider>
      <AuthenticatedSystemProviders>
      <RequestProvider>
        <IdDemandProvider>
              <SourceProvider>
                <PushReprocessProvider>
                  <PushResendCallbacksProvider>
                    <ApiCallbackProvider>
                      <AbrangenciaProvider>
                        <DatateamReprocessProvider>
                          <AutoRequestViewProvider>
                            <RequestViewProvider>
                              <PushViewProvider>
          <AutoRequestProvider>{children}</AutoRequestProvider>
                              </PushViewProvider>
                            </RequestViewProvider>
                          </AutoRequestViewProvider>
                        </DatateamReprocessProvider>
                      </AbrangenciaProvider>
                    </ApiCallbackProvider>
                  </PushResendCallbacksProvider>
                </PushReprocessProvider>
              </SourceProvider>
        </IdDemandProvider>
      </RequestProvider>
      </AuthenticatedSystemProviders>
    </AuthProvider>
      </AntDesignProvider>
    </ThemeProvider>
  );
};

export default Providers;
